import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from './Auth/AuthProvider'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { motion } from 'framer-motion'
import * as invitationService from '../services/invitationService'

const { FiMail, FiShield, FiCheck, FiAlertTriangle, FiLoader } = FiIcons

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  
  const [invitation, setInvitation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [autoAcceptAttempted, setAutoAcceptAttempted] = useState(false)

  const token = searchParams.get('token')
  const hasAutoAccepted = useRef(false)

  // Load invitation when token is available
  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link. Please check your email for the correct link.')
      setLoading(false)
      return
    }

    loadInvitation()
  }, [token])

  // Auto-accept when user is logged in and invitation is loaded
  // This handles the case when user just confirmed their email and was redirected here
  useEffect(() => {
    const shouldAutoAccept = 
      !authLoading && 
      user && 
      invitation && 
      !accepting && 
      !success && 
      !error &&
      !hasAutoAccepted.current &&
      user.email?.toLowerCase() === invitation.email?.toLowerCase()

    if (shouldAutoAccept) {
      console.log('🔄 Auto-accepting invitation for confirmed user...')
      hasAutoAccepted.current = true
      setAutoAcceptAttempted(true)
      handleAccept()
    }
  }, [authLoading, user, invitation, accepting, success, error])

  const loadInvitation = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('🔍 Loading invitation with token:', token)

      const data = await invitationService.getInvitationByToken(token)
      console.log('✅ Invitation loaded:', data)
      setInvitation(data)
    } catch (err) {
      console.error('❌ Error loading invitation:', err)
      // Provide more helpful error messages
      if (err.message?.includes('not found')) {
        setError('This invitation has already been used or does not exist.')
      } else if (err.message?.includes('expired')) {
        setError('This invitation has expired. Please ask for a new invitation.')
      } else {
        setError(err.message || 'Unable to load invitation. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async () => {
    try {
      setAccepting(true)
      setError(null)
      console.log('🔄 Accepting invitation...')

      if (!user) {
        // Redirect to signup with invitation token
        console.log('👤 No user logged in, redirecting to signup...')
        navigate(`/signup-invitation?invitation=${token}`)
        return
      }

      await invitationService.acceptInvitation(token)
      console.log('✅ Invitation accepted successfully!')
      setSuccess(true)

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard')
        window.location.reload() // Reload to update tenant context
      }, 2000)
    } catch (err) {
      console.error('❌ Error accepting invitation:', err)
      // Provide more helpful error messages
      if (err.message?.includes('already a member')) {
        setError('You are already a member of this organization.')
        // Still redirect to dashboard after a moment
        setTimeout(() => {
          navigate('/dashboard')
          window.location.reload()
        }, 2000)
      } else if (err.message?.includes('different email')) {
        setError(`This invitation was sent to ${invitation?.email}. Please sign in with that email address.`)
      } else {
        setError(err.message || 'Failed to accept invitation. Please try again.')
      }
    } finally {
      setAccepting(false)
    }
  }

  // Show loading while auth is checking or invitation is loading
  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading invitation...</p>
        </div>
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
        >
          <SafeIcon icon={FiAlertTriangle} className="text-6xl text-red-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invitation</h1>
          <p className="text-gray-600 mb-6">{error || 'This invitation link is invalid or has expired.'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Go to Home
          </button>
        </motion.div>
      </div>
    )
  }

  // Show accepting state when auto-accept is in progress
  if (accepting && autoAcceptAttempted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
        >
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Joining {invitation?.tenants?.name}...</h1>
          <p className="text-gray-600">Please wait while we set up your account.</p>
        </motion.div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
        >
          <SafeIcon icon={FiCheck} className="text-6xl text-green-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome to {invitation.tenants.name}!</h1>
          <p className="text-gray-600 mb-6">You've successfully joined the team.</p>
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm text-gray-500 mt-4">Redirecting to dashboard...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6 text-white">
          <div className="flex items-center justify-center mb-2">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiMail} className="text-2xl" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center">You're Invited!</h1>
          <p className="text-blue-100 text-center text-sm mt-1">
            Join {invitation.tenants.name}
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="mb-6">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">You've been invited to join:</p>
              <p className="text-xl font-bold text-gray-900">{invitation.tenants.name}</p>
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiMail} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Invited email</p>
                  <p className="font-medium text-gray-900">{invitation.email}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiShield} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Role</p>
                  <p className="font-medium text-gray-900 capitalize">{invitation.role}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiLoader} className="text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Invited by</p>
                  <p className="font-medium text-gray-900">
                    {invitation.invited_by_user?.full_name || 'Admin'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {user ? (
            <div>
              {user.email.toLowerCase() === invitation.email.toLowerCase() ? (
                <button
                  onClick={handleAccept}
                  disabled={accepting}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {accepting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Accepting...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCheck} />
                      <span>Accept Invitation</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-red-600 mb-4">
                    This invitation was sent to {invitation.email}, but you're logged in as {user.email}
                  </p>
                  <button
                    onClick={() => {
                      // Sign out and redirect to login with invitation token
                      navigate(`/login?invitation=${token}`)
                    }}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Sign in with correct account
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={() => navigate(`/signup-invitation?invitation=${token}`)}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Create Account & Accept
              </button>
              <button
                onClick={() => navigate(`/login?invitation=${token}`)}
                className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all"
              >
                Sign In & Accept
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default AcceptInvitation